import React from 'react';

import PropTypes from 'prop-types';

import { gettext } from '@eventbrite/i18n';
import { InfoIcon, InlineAlert, CloseIcon } from '@eventbrite/marmalade';
import classNames from 'classnames';

import { TYPE_ERROR } from '@eventbrite/eds-notification';
import { withMainControls } from '@eventbrite/eds-structure';
import { Link } from '@eventbrite/eds-link';
import { Modal } from '@eventbrite/eds-modal';

import { AlertChunky } from '@eventbrite/eds-iconography';

import PageHeader from '../../components/PageHeader';
import ModalMessage from '../../components/ModalMessage';

import SplitContainer from '../../connectedComponents/SplitContainer';
import ConnectedEventbriteLoginOptions from '../../connectedComponents/EventbriteLoginOptions';
import ConnectedSocialLoginOptions from '../../connectedComponents/ConnectedSocialLoginOptions';
import ToSModal from '../../connectedComponents/ToSModal';
import PasswordlessModal from '../../connectedComponents/PasswordlessModal';

import {
    TOKEN_USED_OR_EXPIRED_MESSAGE,
    TEXT_TOKEN_USED_ONCE,
    TOS_SOCIAL_MUST_BE_ACCEPTED,
} from '../../constants/messages';
import { INVALID_RESET_TOKEN, COMPLETE_SIGN_UP_PATH } from '../../constants';
import { TOSMODAL_SOCIAL_DENIED } from '../../constants/analytics';

import './LoginPage.scss';

/**
 * List of urls that will lead to this page:
 * * BASE_URL/LOG_IN_PATH
 */
class LoginPage extends React.Component {
    static propTypes = {
        clearAuthNotification: PropTypes.func,
        handleSignupClick: PropTypes.func,
        launchAsyncStrategyForAuthSocialScripts: PropTypes.bool,
    };
    state = {
        showExpiredModal: false,
        showInvalidCodeModal: true,
    };
    _handleShowNotification = (error) => {
        const { addMainNotification } = this.props;
        /*Limit the call to addMainNotification to only if
        a notification hasn't been shown since last submit.*/
        if (!this._hasNotification) {
            addMainNotification({
                type: TYPE_ERROR,
                children: error,
                iconType: <AlertChunky />,
            });
            this._hasNotification = true;
        }
    };

    _handleClearNotification = () => {
        const { hideMainNotification } = this.props;
        this._hasNotification = false;
        hideMainNotification && hideMainNotification();
    };

    componentDidMount() {
        const {
            setSignupSecondStep,
            cleanSocialAuthenticationData,
            clearAuthNotification,
            loadGoogleOneTap,
            launchGoogleOneTapWeb,
            setPasswordlessCreateAccountFlow,
            router: {
                location: { query },
            },
        } = this.props;

        if (setSignupSecondStep) {
            setSignupSecondStep(false);
        }
        if (launchGoogleOneTapWeb) {
            //Load google one tap
            loadGoogleOneTap?.();
        }

        //Clear notification and social data when page is loaded.
        clearAuthNotification();
        cleanSocialAuthenticationData();
        setPasswordlessCreateAccountFlow(false);

        /*This is needed here because the route is redirected on the server, before we have the
        chance to set state. SO once the component is loaded we can determine if this page was
        redirected to due to an invalid reset password token*/
        if (query[INVALID_RESET_TOKEN]) {
            this.setState({ showExpiredModal: true });
        }
    }

    _handleGetReferrer = () => {
        if (global.window && window.localStorage) {
            return localStorage.getItem('referrer');
        }
    };

    _onSignupClick = (event) => {
        const { handleSignUpClick } = this.props;

        event?.preventDefault();

        handleSignUpClick?.(null, true);
    };
    _handleToSDenied = () => {
        const { cancelToS } = this.props;

        cancelToS({
            actionName: TOSMODAL_SOCIAL_DENIED,
            notificationMessage: TOS_SOCIAL_MUST_BE_ACCEPTED,
        });
    };
    _handleOnCloseExpiredModal = () => {
        this.setState({ showExpiredModal: false });
    };
    _handleInvalidReferCodeModal = () => {
        this.setState({ showInvalidCodeModal: false });
    };

    render() {
        const {
            acceptSocialToS,
            launchAsyncStrategyForAuthSocialScripts,
            handleTrackBrandLogoClick,
            launchPasswordlessLogin,
            referral_validation_response,
        } = this.props;

        const headerLink = (
            <div className="eds-text-weight--heavy">
                <Link
                    onClick={this._onSignupClick}
                    type="button"
                    data-testid="go-to-signup"
                    to={COMPLETE_SIGN_UP_PATH}
                >
                    {gettext('Sign up')}
                </Link>
            </div>
        );

        const referral_validation = referral_validation_response?.result
            ? referral_validation_response?.result
            : null;

        const pageHeaderProps = {
            headerText: gettext('Log in'),
            usesNewStyles: true,
            handleTrackBrandLogoClick,
            headerLink,
        };
        const buttonCTA = gettext('Log in');
        const optionalSocialProps = launchAsyncStrategyForAuthSocialScripts
            ? { asyncStrategy: 'async' }
            : {};
        const { showExpiredModal, showInvalidCodeModal } = this.state;
        const componentClasses = classNames(
            'eds-l-mar-vert-14',
            'login-status-content',
        );
        const modalLogin = (
            <Modal
                noMinHeight={true}
                isShown={showExpiredModal}
                onClose={this._handleOnCloseExpiredModal}
            >
                <div className="eds-l-pad-all-8 eds-align--center">
                    <div className={componentClasses}>
                        <ModalMessage
                            iconType={<AlertChunky />}
                            headerText={TOKEN_USED_OR_EXPIRED_MESSAGE}
                            expirationTimeText={TEXT_TOKEN_USED_ONCE}
                        />
                    </div>
                </div>
            </Modal>
        );

        const passwordlessModalButton = (
            <div className=" eds-l-mar-bot-3">
                <PasswordlessModal dataTestId="signin-forgot-password" />
            </div>
        );

        const invalidReferralCode = (
            <div
                className="invalid-referral-code-dialog-box eds-l-pad-bot-6 eds-btn--disabled"
                data-whole-div-id="main-error-body"
            >
                <InlineAlert
                    variant="danger"
                    description={
                        <div
                            className="flex-position"
                            data-div-id="access-error-body"
                        >
                            <p data-refer-msg="error-msg-text-body">
                                {gettext(
                                    'Sorry, this referral link is no longer valid.',
                                )}
                            </p>
                            <span
                                onClick={this._handleInvalidReferCodeModal}
                                className="cursor-pointer"
                            >
                                <CloseIcon color="#393246" />
                            </span>
                        </div>
                    }
                    icon={<InfoIcon color="#FFFFFF" />}
                />
            </div>
        );

        const identityFormBody = (
            <SplitContainer
                shouldShowSecondaryPanel={true}
                usesNewStyles={true}
                header={<PageHeader {...pageHeaderProps} />}
            >
                {modalLogin}
                {referral_validation === 'failure' &&
                    showInvalidCodeModal &&
                    referral_validation !== null &&
                    invalidReferralCode}
                <>
                    <ConnectedEventbriteLoginOptions
                        buttonCTA={buttonCTA}
                        handleShowNotification={this._handleShowNotification}
                        handleClearNotification={this._handleClearNotification}
                        referrer={this._handleGetReferrer}
                    />
                    <div className="split-container__divider eds-l-pad-bot-6">
                        <div className="split-container__divider-text eds-text-bm eds-text-weight--heavy">
                            {gettext('or')}
                        </div>
                    </div>
                    {launchPasswordlessLogin && passwordlessModalButton}
                    <div
                        className="eds-l-pad-bot-5"
                        data-testid="social-login-section"
                    >
                        <ConnectedSocialLoginOptions
                            buttonAppleText={gettext('Sign in with Apple')}
                            buttonFBText={gettext('Sign in with Facebook')}
                            buttonGoogleText={gettext('Sign in with Google')}
                            secondarySocialMethods={{
                                facebook: true,
                                apple: true,
                            }}
                            {...optionalSocialProps}
                        />
                    </div>

                    <div className="eds-show-up-md">{headerLink}</div>

                    <ToSModal
                        dataTestId="signup-tos"
                        onAcceptToS={acceptSocialToS}
                        onCancelToS={this._handleToSDenied}
                    />
                </>
            </SplitContainer>
        );

        return <>{identityFormBody}</>;
    }
}

export default withMainControls(LoginPage);
